import React, { useState, useMemo } from 'react';

// 🚀 데이터는 MobileMain 에서 한 번만 받아 props 로 내려준다.
//    분석 탭에서 다시 다운로드하지 않으므로(중복 제거) 탭 전환이 즉시 이뤄진다.
export default function MobileAnalysis({ asRecords = [], vehicles = [], parts = [], customerCount = 0, loading = false }) {
  // 1. 5개 카테고리 모바일 탭 상태 제어
  const [activeCategory, setActiveCategory] = useState('SALES');
  const categories = [
    { id: 'SALES', label: '💰 매출분석' },
    { id: 'PARTS', label: '📦 부품분석' },
    { id: 'AS', label: '🛠️ AS분석' },
    { id: 'CUSTOMER', label: '👥 고객분석' },
    { id: 'VEHICLE', label: '🚗 차량분석' }
  ];

  // 🚀 받은 데이터로 통계를 계산한다(PC 버전 Analysis.js 와 동일 규칙). props 가 바뀌면 자동 재계산.
  const stats = useMemo(() => {
    const safeVehicles = vehicles || [];
    const safeParts = parts || [];
    const safeAsRecords = asRecords || [];

    // --- [A. 날짜 기본 규격] ---
    const nowObj = new Date();
    const y = nowObj.getFullYear();
    const m = String(nowObj.getMonth() + 1).padStart(2, '0');
    const d = String(nowObj.getDate()).padStart(2, '0');
    const todayStr = `${y}-${m}-${d}`;

    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(nowObj.getMonth() + 3);

    // --- [B. 보증 만료 예측] ---
    let expiringSoon = 0;
    let expired = 0;
    let active = 0;
    safeVehicles.forEach(v => {
      if (!v.warrantyExpiry || String(v.warrantyExpiry).includes('NaN') || v.warrantyExpiry === '-') {
        expired++;
        return;
      }
      const expDate = new Date(v.warrantyExpiry);
      if (expDate < nowObj) expired++;
      else if (expDate >= nowObj && expDate <= threeMonthsLater) expiringSoon++;
      else active++;
    });

    // --- [C. AS 정비 상태 / 다발성 결함 원인] ---
    let todaySalesAmt = 0;
    let totalSalesAmt = 0;
    let receiptCount = 0;
    let completedCount = 0;
    let progressingCount = 0;
    let pendingCount = 0;
    let freeCount = 0;
    let paidCount = 0;
    const symptomMap = {};

    safeAsRecords.forEach(a => {
      // 금액 기준을 PC 분석(utils/analysisHelpers.js)·홈 화면과 똑같이 맞춘다. (2026-09-23)
      //   예전에는 여기서만 totalCost 를 그대로 더해서 VAT 가 섞였고, 게다가 접수·진행중·보류까지
      //   전부 합산했다. 그래서 홈의 '연간 매출 총계(공급가액)' 와 분석의 '누적' 이 서로 달랐다.
      const amtWithVat = Number(a.totalCost || a.totalAmount || a.totalPrice || 0) + (Number(a.discount) || 0); // 할인 전 총액
      const amt = Math.round(amtWithVat / 1.1); // 부가세 제외 공급가액

      const status = a.status || '접수';
      const date = a.settleDate || a.asDate || '';

      // 매출로 인정하는 것은 '완료' 된 건뿐이다 (PC 와 같다)
      if (status === '완료') totalSalesAmt += amt;

      if (date === todayStr && (status === '완료' || status === '진행중')) {
        todaySalesAmt += amt;
      }

      if (status === '완료') completedCount++;
      else if (status === '진행중') progressingCount++;
      else if (status === '보류') pendingCount++;
      else receiptCount++;

      const rType = a.repairType || '유상';
      if (rType === '무상' || amt === 0) freeCount++;
      else paidCount++;

      const symptom = a.symptom || '일반 정비/점검';
      const cleanSymptom = symptom.split('\n')[0].trim().substring(0, 18);
      if (cleanSymptom) {
        symptomMap[cleanSymptom] = (symptomMap[cleanSymptom] || 0) + 1;
      }
    });

    // 다발성 결함 증상순 정렬 가공 TOP 5 상위 노출
    const topSymptoms = Object.keys(symptomMap).map(name => ({
      name,
      count: symptomMap[name],
      percentage: safeAsRecords.length ? Math.round((symptomMap[name] / safeAsRecords.length) * 100) : 0
    })).sort((a, b) => b.count - a.count).slice(0, 5);

    // --- [D. 물류 부품 평가액] ---
    let totalPartsValue = 0;
    let lowStockCount = 0;
    safeParts.forEach(p => {
      totalPartsValue += (Number(p.stock) || 0) * (Number(p.purchasePrice) || 0);
      if ((Number(p.stock) || 0) <= 3) lowStockCount++;
    });

    return {
      todaySales: todaySalesAmt,
      totalSales: totalSalesAmt,
      settleCount: completedCount,
      totalPartsValue,
      lowStockCount,
      asTotal: safeAsRecords.length,
      asStatus: { receipt: receiptCount, completed: completedCount, progressing: progressingCount, pending: pendingCount },
      asType: { free: freeCount, paid: paidCount },
      customerTotal: Number(customerCount) || 0,
      vehicleTotal: safeVehicles.length,
      warrantyData: { expiringSoon, expired, active },
      topSymptoms
    };
  }, [asRecords, vehicles, parts, customerCount]);

  // 카테고리 터치 이동식 하이브리드 UI 분기 렌더러
  const renderCategoryContent = () => {
    if (loading) {
      return <div style={{ textAlign: 'center', color: '#64748b', padding: '40px 0', fontSize: '13px' }}>🔄 실시간 경영 지표 연동 중...</div>;
    }

    switch (activeCategory) {
      case 'SALES': // 💰 1. 매출분석 세그먼트
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ background: '#1e293b', padding: '20px', borderRadius: '16px', color: '#ffffff', boxShadow: '0 4px 6px rgba(0,0,0,0.04)' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#94a3b8', fontWeight: 'bold' }}>💰 일일 정산 금액 (오늘 마감, 공급가)</p>
              <p style={{ margin: '0', fontSize: '24px', fontWeight: '900', color: '#38bdf8' }}>₩ {stats.todaySales.toLocaleString()} 원</p>
            </div>
            <div style={{ background: '#10b981', padding: '20px', borderRadius: '16px', color: '#ffffff', boxShadow: '0 4px 6px rgba(0,0,0,0.04)' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#e6f4ea', fontWeight: 'bold' }}>💳 전체 누적 매출 총액 (공급가)</p>
              <p style={{ margin: '0', fontSize: '24px', fontWeight: '900' }}>₩ {stats.totalSales.toLocaleString()} 원</p>
            </div>
            <div style={{ background: '#6366f1', padding: '20px', borderRadius: '16px', color: '#ffffff', boxShadow: '0 4px 6px rgba(0,0,0,0.04)' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#e0e7ff', fontWeight: 'bold' }}>📊 매출 마감 완결 건수</p>
              <p style={{ margin: '0', fontSize: '24px', fontWeight: '900' }}>{stats.settleCount} 건</p>
            </div>
          </div>
        );

      case 'PARTS': // 📦 2. 부품분석 세그먼트
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ background: '#ffffff', padding: '18px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.01)' }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>📦 총 물류 재고 자산 평가액</p>
              <p style={{ margin: '0', fontSize: '22px', fontWeight: '900', color: '#1e293b' }}>₩ {stats.totalPartsValue.toLocaleString()} 원</p>
            </div>
            <div style={{ background: '#fef2f2', padding: '18px', borderRadius: '16px', border: '1px solid #fecaca', borderLeft: '6px solid #ef4444' }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#991b1b', fontWeight: '700' }}>🚨 긴급 재고부족 알림 대상</p>
              <p style={{ margin: '0', fontSize: '22px', fontWeight: '900', color: '#ef4444' }}>{stats.lowStockCount} 품목</p>
            </div>
          </div>
        );

      case 'AS': // 🛠️ 3. AS분석 세그먼트 (다발성 고장원인 결합)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ background: '#ffffff', padding: '18px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>⚙️ 실시간 현장 공정 상태 공정비</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', textAlign: 'center' }}>
                <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '8px' }}><div style={{ fontSize: '11px', color: '#64748b' }}>접수대기</div><div style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>{stats.asStatus.receipt}건</div></div>
                <div style={{ background: '#fff7ed', padding: '10px', borderRadius: '8px' }}><div style={{ fontSize: '11px', color: '#ea580c' }}>정비진행</div><div style={{ fontSize: '16px', fontWeight: '800', color: '#ea580c' }}>{stats.asStatus.progressing}건</div></div>
                <div style={{ background: '#f0fdf4', padding: '10px', borderRadius: '8px' }}><div style={{ fontSize: '11px', color: '#16a34a' }}>정산마감</div><div style={{ fontSize: '16px', fontWeight: '800', color: '#16a34a' }}>{stats.asStatus.completed}건</div></div>
                <div style={{ background: '#fef2f2', padding: '10px', borderRadius: '8px' }}><div style={{ fontSize: '11px', color: '#dc3545' }}>보류요망</div><div style={{ fontSize: '16px', fontWeight: '800', color: '#dc3545' }}>{stats.asStatus.pending}건</div></div>
              </div>
            </div>

            {/* 다발성 고장 원인 리스트 TOP 5 */}
            <div style={{ background: '#ffffff', padding: '18px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#1e293b', fontWeight: '800' }}>🔥 다발성 결함 요인 TOP 5</p>
              {stats.topSymptoms.map((item, idx) => (
                <div key={idx} style={{ marginBottom: '10px', borderBottom: idx === 4 ? 'none' : '1px solid #f1f5f9', paddingBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '700', color: '#334155' }}>{idx + 1}위. {item.name}</span>
                    <span style={{ color: '#007bff', fontWeight: 'bold' }}>{item.count}건 ({item.percentage}%)</span>
                  </div>
                  <div style={{ width: '100%', height: '5px', background: '#f1f5f9', borderRadius: '3px' }}>
                    <div style={{ width: `${item.percentage}%`, height: '100%', background: '#3b82f6', borderRadius: '3px' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'CUSTOMER': // 👥 4. 고객분석 세그먼트
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.01)' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>👥 CRM 마스터 데이터 유효 계정 회원</p>
              <p style={{ margin: '0', fontSize: '24px', fontWeight: '900', color: '#2563eb' }}>{stats.customerTotal} 명</p>
            </div>
          </div>
        );

      case 'VEHICLE': // 🚗 5. 차량분석 세그먼트 (보증 예측 포함)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ background: '#ffffff', padding: '18px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>🚗 R&D 관제 총 유통 차량대수</p>
              <p style={{ margin: '0', fontSize: '22px', fontWeight: '900', color: '#1e293b' }}>{stats.vehicleTotal} 대</p>
            </div>

            <div style={{ background: '#ffffff', padding: '18px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <p style={{ margin: '0 0 ' + '12px 0', fontSize: '13px', color: '#64748b', fontWeight: '700' }}>🚨 선제적 보증 만료 모니터링 예측</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#fffbeb', color: '#b45309', borderRadius: '8px', fontWeight: '700' }}>
                  <span>⚠️ 3개월 이내 보증 만료 임박</span><span>{stats.warrantyData.expiringSoon} 대</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#fef2f2', color: '#b91c1c', borderRadius: '8px', fontWeight: '700' }}>
                  <span>🚫 보증 기간 최종 만료 (유상전환)</span><span>{stats.warrantyData.expired} 대</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f0fdf4', color: '#15803d', borderRadius: '8px', fontWeight: '700' }}>
                  <span>✅ 보증 안심 정상 운행 대상</span><span>{stats.warrantyData.active} 대</span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ padding: '16px', boxSizing: 'border-box', backgroundColor: '#f8fafc' }}>

      {/* 🚀 상단 카테고리 5대 슬라이드 터치 탭 메뉴바 바인딩 */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        overflowX: 'auto',
        paddingBottom: '8px',
        WebkitOverflowScrolling: 'touch'
      }}>
        {categories.map((cat) => {
          const isSelected = activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              style={{
                background: isSelected ? '#1e293b' : '#ffffff',
                color: isSelected ? '#ffffff' : '#64748b',
                padding: '10px 18px',
                borderRadius: '20px',
                border: isSelected ? 'none' : '1px solid #e2e8f0',
                fontSize: '13px',
                fontWeight: '700',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                boxShadow: isSelected ? '0 4px 10px rgba(30,41,59,0.15)' : '0 2px 4px rgba(0,0,0,0.02)',
                outline: 'none',
                transition: 'all 0.15s'
              }}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* 가공 연동 완료 모듈 컴포넌트 출력 */}
      <div>
        {renderCategoryContent()}
      </div>

    </div>
  );
}
